import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Card, EmptyState, ErrorState, Spinner, cx } from '@/components/ui';
import { formatDate, formatDateTime, formatKrw, formatPercent, toThousands } from '@/lib/format';
import {
  HitProbability,
  IndicatorGrid,
  ProbabilityDisclaimer,
  ResultBadge,
  ScoreBar,
} from './components';
import type { Indicator, Recommendation, Source, StockAnalysis } from './types';
import { useRecommendation } from './useTrading';

/**
 * 추천 상세.
 *
 * 이 화면이 이 프로젝트의 결과물이 모이는 자리다.
 *   지표 계산(4단계) · 스코어링(5단계) · 백테스트(7단계) · LLM 분석(9단계)
 *
 * 🔴 숫자와 문장의 출처를 섞어 보여주지 않는다.
 *   목표가·손절가·점수·도달률은 지표와 백테스트가 만든 값이고,
 *   요약·긍정·부정은 LLM 이 쓴 문장이다. 둘을 한 덩어리로 보여주면
 *   나중에 "이 목표가는 AI 가 정한 건가?" 를 알 수 없게 된다.
 *   그래서 카드를 나누고 각 카드에 출처를 적어둔다.
 */
export function RecommendationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useRecommendation(Number(id));

  if (query.isLoading) {
    return <Spinner />;
  }
  if (query.error) {
    return (
      <div className="flex flex-col gap-3">
        <BackLink />
        <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
      </div>
    );
  }
  if (!query.data) {
    return (
      <div className="flex flex-col gap-3">
        <BackLink />
        <EmptyState title="추천을 찾을 수 없습니다." />
      </div>
    );
  }

  const item = query.data;

  return (
    <div className="flex flex-col gap-4">
      <BackLink />

      <Summary item={item} />
      <Prices item={item} />
      {item.result && <Evaluation item={item} />}
      {item.indicators && <Indicators indicators={item.indicators} />}
      <Analysis analysis={item.analysis} />
      {item.sources.length > 0 && <Sources sources={item.sources} />}

      <ProbabilityDisclaimer />
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/trading" className="text-xs text-fg-muted hover:text-fg">
      ← 추천 목록
    </Link>
  );
}

// ─────────────────────────────────────────────
// 요약
// ─────────────────────────────────────────────

function Summary({ item }: { item: Recommendation }) {
  return (
    <Card>
      <header className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-fg">{item.name}</h2>
          <p className="text-xs text-fg-subtle">
            {item.symbol} · {formatDateTime(item.recommendedAt)} 추천
          </p>
        </div>
        <ResultBadge result={item.result} />
      </header>

      <div className="mt-4">
        <p className="mb-1 text-xs text-fg-subtle">스코어 (0~100, 55점 이상만 추천)</p>
        <ScoreBar score={item.score} />
      </div>

      <div className="mt-4">
        <p className="mb-1 text-xs text-fg-subtle">과거 도달률</p>
        <HitProbability probability={item.hitProbability} samples={item.sampleSize} />
      </div>

      {item.evaluateOn && !item.result && (
        <p className="mt-4 text-xs text-fg-muted">
          {formatDate(item.evaluateOn)}까지 목표가·손절가 도달 여부를 채점합니다.
        </p>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────
// 가격
// ─────────────────────────────────────────────

function Prices({ item }: { item: Recommendation }) {
  return (
    <Card title="가격">
      <div className="grid grid-cols-3 gap-3 text-center">
        <PriceCell label="진입" price={item.entryPrice} />
        <PriceCell
          label="목표"
          price={item.targetPrice}
          percent={item.targetPercent}
          tone="text-rise"
        />
        <PriceCell
          label="손절"
          price={item.stopLossPrice}
          percent={item.stopLossPercent}
          tone="text-fall"
        />
      </div>

      {/*
        진입가가 추천 시점 종가라는 것을 밝혀둔다.
        실제로 그 가격에 살 수 있었는지는 별개다 — 다음 날 시가가 갭으로 뛸 수 있다.
      */}
      <p className="mt-3 text-xs text-fg-subtle">
        진입가는 추천 시점의 종가입니다. 실제 체결가와 다를 수 있습니다.
      </p>
    </Card>
  );
}

function PriceCell({
  label,
  price,
  percent,
  tone,
}: {
  label: string;
  price: number;
  percent?: number;
  tone?: string;
}) {
  return (
    <div>
      <p className="text-xs text-fg-subtle">{label}</p>
      <p className={cx('text-sm font-semibold tabular', tone ?? 'text-fg')}>
        {formatKrw(price)}
      </p>
      {percent !== undefined && (
        <p className={cx('text-xs tabular', tone ?? 'text-fg-muted')}>
          {formatPercent(percent)}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// 채점 결과
// ─────────────────────────────────────────────

/**
 * 사후 채점.
 *
 * 🔴 최대상승·최대하락을 함께 보여준다.
 *   결과가 LOSS 여도 그 사이에 몇 % 까지 올랐었는지가 남는다.
 *   "손절 직전에 목표 근처까지 갔었다" 와 "한 번도 안 올랐다" 는 완전히 다른 실패다.
 *   15단계 패턴분석이 이 차이를 본다.
 */
function Evaluation({ item }: { item: Recommendation }) {
  return (
    <Card title="채점 결과">
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Row label="판정" value={<ResultBadge result={item.result} />} />
        <Row
          label="채점 시각"
          value={item.evaluatedAt ? formatDateTime(item.evaluatedAt) : '-'}
        />
        <Row label="판정 가격" value={formatKrw(item.resultPrice)} />
        <Row
          label="수익률"
          value={
            <span
              className={cx(
                'tabular font-semibold',
                (item.returnPercent ?? 0) > 0 ? 'text-rise' : 'text-fall',
              )}
            >
              {formatPercent(item.returnPercent)}
            </span>
          }
        />
        <Row
          label="최대 상승"
          value={<span className="tabular text-rise">{formatPercent(item.maxGainPercent)}</span>}
        />
        <Row
          label="최대 하락"
          value={<span className="tabular text-fall">{formatPercent(item.maxDrawdownPercent)}</span>}
        />
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="mt-0.5 text-fg">{value}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────
// 지표
// ─────────────────────────────────────────────

/**
 * 계산된 지표.
 *
 * 목록 자체는 components.tsx 의 IndicatorGrid 로 옮겼다 (2026-08-16).
 * 종목 상세 모달(16단계)이 같은 목록을 쓰기 때문이다.
 * 두 곳에 각자 두면 임계값 설명이 갈린다. (CLAUDE.md 5.2절)
 *
 * 여기 남은 것은 이 화면의 문맥 — 기준일 제목과 출처 문구다.
 */
function Indicators({ indicators }: { indicators: Indicator }) {
  return (
    <Card title={`지표 · ${formatDate(indicators.baseDate)} 기준`}>
      <IndicatorGrid indicator={indicators} />

      <p className="mt-4 text-xs text-fg-subtle">
        이 숫자들은 일봉으로 계산한 값입니다. AI가 만든 값이 아닙니다.
      </p>
    </Card>
  );
}

// ─────────────────────────────────────────────
// LLM 분석
// ─────────────────────────────────────────────

const CONFIDENCE_LABEL = {
  HIGH: '자료 충분',
  MEDIUM: '자료 보통',
  LOW: '자료 부족',
} as const;

/**
 * LLM 분석.
 *
 * 🔴 출처 없는 분석은 애초에 저장되지 않는다. (CLAUDE.md 1.3절)
 *   응답의 모든 url 을 입력 자료와 대조해 하나라도 없으면 분석 전체를 폐기한다.
 *   그래서 여기 보이는 링크는 전부 실제로 수집된 자료다.
 *   그 검증이 코드에 있다는 것을 화면에서도 밝힌다.
 *
 * 분석이 없는 경우가 흔하다 — 수집된 자료가 0건이거나, 예산 가드에 걸렸거나,
 * API 키가 없는 환경이다. 그것을 오류로 보여주지 않는다.
 */
function Analysis({ analysis }: { analysis: StockAnalysis | null }) {
  if (!analysis) {
    return (
      <Card title="AI 분석">
        <p className="text-xs text-fg-muted">
          {/*
            ✏️ 9/28 I-78 2차 — 막는 조건이 둘 빠져 있었다: 하루 호출 한도 · 같은 종목 6시간 쿨다운(AiUsageService).
              세 프로필이 같은 날 같은 종목을 추천하면 두 번째부터 쿨다운에 걸릴 수 있다[추론 — 운영 데이터 미확인]
          */}
          이 추천에는 AI 분석이 없습니다. 수집된 뉴스·공시가 없었거나, 출처 검증을
          통과하지 못해 폐기되었거나, 월 예산 · 하루 호출 한도에 걸렸거나, 같은 종목을 6시간 안에
          이미 분석한 경우입니다(다른 계좌의 같은 날 추천에 분석이 있을 수 있습니다).
        </p>
      </Card>
    );
  }

  return (
    <Card
      title="AI 분석"
      action={
        <Badge tone={analysis.confidence === 'LOW' ? 'warning' : 'neutral'}>
          {CONFIDENCE_LABEL[analysis.confidence]}
        </Badge>
      }
    >
      <p className="text-sm leading-relaxed text-fg">{toThousands(analysis.summary)}</p>

      {analysis.positives.length > 0 && (
        <PointList title="긍정 요인" tone="text-rise" points={analysis.positives} />
      )}
      {analysis.negatives.length > 0 && (
        <PointList title="부정 요인" tone="text-fall" points={analysis.negatives} />
      )}

      {analysis.sources.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 text-xs font-medium text-fg-muted">근거</p>
          <ul className="flex flex-col gap-1">
            {analysis.sources.map((source) => (
              <li key={source.url}>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-link underline underline-offset-2"
                >
                  {source.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 text-xs text-fg-subtle">
        위 문장은 AI가 수집된 자료를 읽고 쓴 것입니다. 목표가·손절가·점수·도달률은 여기서
        나오지 않습니다. 출처가 확인되지 않은 분석은 저장되지 않습니다.
      </p>
    </Card>
  );
}

function PointList({
  title,
  tone,
  points,
}: {
  title: string;
  tone: string;
  points: string[];
}) {
  return (
    <div className="mt-4">
      <p className={cx('mb-1 text-xs font-medium', tone)}>{title}</p>
      <ul className="flex flex-col gap-1">
        {points.map((point) => (
          <li key={point} className="flex gap-2 text-sm text-fg">
            <span className="text-fg-subtle">·</span>
            <span>{toThousands(point)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────
// 수집 자료
// ─────────────────────────────────────────────

/**
 * 이 추천을 만들 때 수집된 뉴스·공시 전체.
 *
 * AI 분석의 "근거" 와 다르다. 저쪽은 AI 가 실제로 인용한 것이고
 * 이쪽은 입력으로 넣은 것 전부다. 무엇을 보고 무엇을 안 봤는지가 드러난다.
 */
function Sources({ sources }: { sources: Source[] }) {
  return (
    <Card title={`수집 자료 ${sources.length}건`}>
      <ul className="flex flex-col gap-3">
        {sources.map((source) => (
          <li key={source.id}>
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-link underline underline-offset-2"
            >
              {source.title}
            </a>
            <p className="text-xs text-fg-subtle">
              {source.sourceType === 'DISCLOSURE' ? '공시' : '뉴스'}
              {source.publisher && ` · ${source.publisher}`}
              {source.publishedAt && ` · ${formatDateTime(source.publishedAt)}`}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
